import { Component } from 'preact'
import { extractRenderPropsChildren } from '../../../libs/renderProps'
import { BaseFilterSelectorBuilderData, FilterSelectorProps } from './interfaces'
import FilterOption, { FilterOptionItem } from './FilterOption'
import InputGroup from '@insided/deprecated-control/ui-components/InputGroup'
import SearchBox from '@insided/deprecated-preact-components/search-box/SearchBox'
import { FilterSelector } from '@insided/deprecated-control/overview/filters/FiltersMenu'

export interface CheckboxFilterSelectorCondition {
  id: string | number
  name: string
  value: string
  showItems: boolean
}

interface CheckboxFilterSelectorItems {
  items: FilterOptionItem[]
  conditions: CheckboxFilterSelectorCondition[]
}

interface State {
  condition: string
  showItems: boolean
  data: FilterOptionItem[]
  filterValue: string
  showAllItems: boolean
}

interface CommonProps {
  isSearchable?: boolean
  itemsThreshold?: number
}

export interface CheckboxFilterSelectorProps extends FilterSelectorProps<State>, CommonProps {
  items: CheckboxFilterSelectorItems
}

interface BuilderData extends BaseFilterSelectorBuilderData, CommonProps {
  items: FilterOptionItem[]
  conditions: CheckboxFilterSelectorCondition[]
}

interface Filter extends FilterSelector, CommonProps {
  items: CheckboxFilterSelectorItems
}

class CheckboxFilterSelector extends Component<CheckboxFilterSelectorProps, State> {
  static create = (config: BuilderData): Filter => {
    const { items, conditions, ...rest } = config

    return {
      component: this,
      type: 'filter',
      items: {
        items,
        conditions
      },
      getPillDisplayValue: ({ condition, showItems, data }: State): string => {
        const rawCondition = conditions.find(c => c.value === condition)
        let firstItem = ''
        let otherItemsCount = ''
        if (showItems && data.length) {
          firstItem = data[0].name
          otherItemsCount = data.length > 1
            ? `+${data.length - 1}`
            : ''
        }

        return `${rawCondition?.name} ${firstItem} ${otherItemsCount}`
      },
      ...rest
    }
  }

  constructor(props: CheckboxFilterSelectorProps) {
    super(props)

    const { items: { conditions: [firstCondition] } } = this.props

    this.state = {
      condition: props.selectedFilterItems?.condition || firstCondition.value || '',
      showItems: props.selectedFilterItems?.showItems || firstCondition.showItems || false,
      data: props.selectedFilterItems?.data || [],
      filterValue: '',
      showAllItems: false
    }
  }

  onFilterOptionSelection = (selectedItems: string[]) => {
    const { items } = this.props.items
    selectedItems = selectedItems.map(selected => String(selected))
    this.setState({
      data: items.filter(item => selectedItems.includes(String(item.id)))
    })
  }

  setCondition = (condition: string, showItems: boolean) => {
    this.setState({ condition, showItems, showAllItems: false, filterValue: '' })
  }

  getMaxItemsCount = (): number => {
    const { itemsThreshold = 5 } = this.props

    return Math.max(0, itemsThreshold)
  }

  shouldShowAllItems = (totalFilteredItems: number): boolean => {
    const maxItemsCount = this.getMaxItemsCount()

    return !maxItemsCount || totalFilteredItems <= maxItemsCount
  }

  getItemsToRender = (): FilterOptionItem[] => {
    const { items: { items } } = this.props
    const { filterValue, showAllItems } = this.state

    const filteredItems = items.filter(item => item.name.toLowerCase().includes(filterValue.toLowerCase()))

    if (showAllItems || this.shouldShowAllItems(filteredItems.length)) {
      return filteredItems
    }

    return filteredItems.slice(0, this.getMaxItemsCount())
  }

  showAllItemsButton = () => {
    const { items: { items } } = this.props
    const { showAllItems, filterValue } = this.state
    const filteredItems = items.filter(item => item.name.toLowerCase().includes(filterValue.toLowerCase()))

    return !showAllItems && !this.shouldShowAllItems(filteredItems.length) && (
      <p className='select-all' onClick={() => this.setState({ showAllItems: true })}>Show all</p>
    )
  }

  render() {
    const { name, items: { conditions }, children, isSearchable = true } = this.props
    const { condition: currentCondition, showItems, data, filterValue } = this.state

    return (
      <div>
        <div className='overview__filters-box-content'>
          {conditions.map(condition => {
            const checked = currentCondition === condition.value
            return (
              <div key={condition.id}>
                <InputGroup
                  inline
                  qaClasses={`qa-filter-input-${condition.value}`}
                  key={condition.id}
                  name={name}
                  value={condition.value}
                  title={condition.name}
                  type='radio'
                  formGroupClasses='overview__filters-box-filter'
                  checked={checked}
                  onChange={(e) => this.setCondition(e.target.value, condition.showItems)}
                />
                {checked && condition.showItems && (
                  <div className='indent'>
                    {isSearchable && (
                      <SearchBox
                        isModerationOverview
                        placeholder={`Search for ${name}`}
                        feature='auto-size'
                        value={filterValue}
                        onSubmit={() => {}}
                        onInput={(e) => this.setState({ filterValue: (e.target as HTMLInputElement).value })}
                        onClear={() => this.setState({ filterValue: '' })}
                      />
                    )}
                    {this.getItemsToRender().map((item: FilterOptionItem) => {
                      return (
                        <FilterOption
                          item={item}
                          key={item.id}
                          type='checkbox'
                          data={data.map(item => String(item.value))}
                          onFilterOptionSelection={this.onFilterOptionSelection}
                        />
                      )
                    })}
                    {this.showAllItemsButton()}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {extractRenderPropsChildren(children)({
          data: {
            condition: currentCondition,
            showItems,
            data: showItems ? data : []
          },
          isApplyButtonDisabled: !currentCondition || (showItems && data.length === 0)
        })}
      </div>
    )
  }
}

export default CheckboxFilterSelector
